/**
 * TTO product line-up (Nov 1, 2026) — one list for the Products menu, the home
 * page product cards and the footers, so the names read the same everywhere.
 *
 * Order runs from the smallest group to the biggest organization. A product
 * shows on the website only when `live` is true — no "coming soon" (Dennis,
 * Oct 2 / Nov 1). To launch Leagues, Associations or Clubs: build its page,
 * set its `to`, flip `live`, and it appears in the menu, the home cards and
 * the footers at once. Four or five live products switch the home cards to
 * compact tiles automatically (LandingPage.js).
 *
 * Plan doc: claude/Products_Menu_And_Home_Page_Plan_Nov01.md
 */

export const PRODUCTS = [
  {
    key: 'groups',
    live: true,
    to: '/groups',
    name: 'Weekly Golf Groups',
    line: 'Your regular game, whether three times a week or twice a month.',
    voice: 'I run a weekly game with my regulars.',
    bullets: [
      'Tee times, sign-ups and waitlists',
      'Pairings, games and live scoring',
      'Stats, round recaps and GHIN posting',
    ],
    price: 'Groups of any size, up to 250 players · free to start',
    cta: 'See plans & pricing',
  },
  {
    key: 'leagues',
    live: false,
    to: '/leagues',
    name: 'Leagues',
    line: 'Weekly league rounds, games and season standings.',
    voice: 'Our league plays every week and keeps season standings.',
    bullets: [],
    price: '',
    cta: 'See Leagues',
  },
  {
    key: 'events',
    live: true,
    to: '/events',
    name: 'Tournaments & Outings',
    line: 'Member-guests, charity outings, club championships.',
    voice: 'I’m running a tournament, outing or member-guest.',
    bullets: [
      'Registration and team sign-up',
      'Shotgun pairings, cart signs and scorecards',
      'Live leaderboard on phones and TVs',
    ],
    price: 'Event Pass from $135 · no subscription',
    cta: 'See Event Pass pricing',
  },
  {
    key: 'associations',
    live: false,
    to: '/associations',
    name: 'Associations',
    line: 'For MGAs and LGAs: the roster and the season’s events.',
    voice: 'Our MGA or LGA runs its roster and its events.',
    bullets: [],
    price: '',
    cta: 'See Associations',
  },
  {
    key: 'clubs',
    live: false,
    to: '/clubs',
    name: 'Clubs',
    line: 'Club events, its associations and its member groups, one account.',
    voice: 'We’re a club running events, associations and member groups.',
    bullets: [],
    price: '',
    cta: 'See Clubs',
  },
];

export const LIVE_PRODUCTS = PRODUCTS.filter((p) => p.live);

// ---------------------------------------------------------------------------
// Per-route meta for the marketing pages. public/index.html carries the home
// values as the site default; a product page applies its own on mount and puts
// HOME_META back on unmount. The prerenderer captures the DOM after render, so
// each prerendered page ships with its own tags.
// ---------------------------------------------------------------------------
export const HOME_META = {
  title: 'Golf Group & Tournament Organizer | Tee Time Organizer',
  description:
    'Organize your weekly golf group or run your tournament: tee times and one-tap sign-ups, pairings, games, live scoring, player stats and GHIN posting. Free to start for weekly groups; one Event Pass for a tournament.',
  url: 'https://teetimeorganizer.net',
};

export const GROUPS_META = {
  title: 'Weekly Golf Group Organizer App | Tee Time Organizer',
  description:
    'The golf group organizer built for the person who runs the weekly game. Tee times, one-tap RSVPs, waitlists, pairings, games, live scoring and GHIN posting — no spreadsheets, no group texts. Free for up to 8 players; plans for groups up to 250.',
  url: 'https://teetimeorganizer.net/groups',
};

export const applyMeta = (meta) => {
  if (typeof document === 'undefined') return;
  const set = (sel, attr, value) => {
    const el = document.querySelector(sel);
    if (el) el.setAttribute(attr, value);
  };
  document.title = meta.title;
  set('meta[name="title"]', 'content', meta.title);
  set('meta[name="description"]', 'content', meta.description);
  set('meta[name="twitter:title"]', 'content', meta.title);
  set('meta[name="twitter:description"]', 'content', meta.description);
  set('meta[name="twitter:url"]', 'content', meta.url);
  set('meta[property="og:title"]', 'content', meta.title);
  set('meta[property="og:description"]', 'content', meta.description);
  set('meta[property="og:url"]', 'content', meta.url);
  set('link[rel="canonical"]', 'href', meta.url);
};
